import React, {useCallback, useEffect, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {push} from 'connected-react-router';
import {Icon, Label, Menu, Popup} from 'semantic-ui-react';
import {toast} from 'react-toastify';
import {fetchConversations, isMessageUnread} from '../../modules/message';

const CHAT_POLL_INTERVAL_MS = 60000;
const MAX_NOTIFIED_MESSAGE_IDS = 100;

const showIncomingMessageToast = ({conversation, message, t, changePage}) => {
  const senderName = message.from?.displayName || t('Someone');
  const conversationUrl = `/inbox/conversations/${conversation._id}`;
  toast.info(
    <span>
      {t('NewMessageFromX', {x: senderName})}{' '}
      <a
        href={conversationUrl}
        className='Toastify__toast-action'
        style={{color: 'inherit', fontWeight: 700, textDecoration: 'underline'}}
      >
        {t('Open')}
      </a>
    </span>,
    {
      closeOnClick: false,
      onClick: () => changePage(conversationUrl)
    }
  );
};

export const ChatMenuItem = ({
  t,
  userProfile,
  idToken,
  unreadCount = 0,
  latestIncomingMessage,
  changePage,
  fetchConversations
}) => {
  const userId = userProfile?._id;
  const notifiedMessageIds = useRef(new Set());
  const notifyIncomingMessage = useCallback(
    ({conversation, message}) => {
      if (message?._id) {
        if (notifiedMessageIds.current.has(message._id)) {
          return;
        }
        notifiedMessageIds.current.add(message._id);
        if (notifiedMessageIds.current.size > MAX_NOTIFIED_MESSAGE_IDS) {
          const oldestMessageId = notifiedMessageIds.current.values().next()
            .value;
          notifiedMessageIds.current.delete(oldestMessageId);
        }
      }
      showIncomingMessageToast({conversation, message, t, changePage});
    },
    [changePage, t]
  );

  useEffect(() => {
    if (
      !latestIncomingMessage ||
      !isMessageUnread(latestIncomingMessage, userId)
    ) {
      return;
    }

    const conversationId =
      typeof latestIncomingMessage.conversation === 'string'
        ? latestIncomingMessage.conversation
        : latestIncomingMessage.conversation?._id;
    notifyIncomingMessage({
      conversation: {_id: conversationId},
      message: latestIncomingMessage
    });
  }, [latestIncomingMessage, notifyIncomingMessage, userId]);

  useEffect(() => {
    if (!userProfile || !idToken || !fetchConversations) {
      return undefined;
    }

    const refresh = notifyOnNew =>
      fetchConversations(idToken, {
        notifyOnNew,
        onIncomingMessage: notifyIncomingMessage
      });

    refresh(false);
    const timer = window.setInterval(
      () => refresh(true),
      CHAT_POLL_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, [fetchConversations, idToken, notifyIncomingMessage, userProfile]);

  if (!userProfile) {
    return null;
  }

  const label = t('Chat');
  const trigger = (
    <Menu.Item
      as='button'
      type='button'
      fitted
      icon
      aria-label={label}
      title={label}
      onClick={() => changePage('/inbox')}
      style={{
        minWidth: 44,
        minHeight: 44,
        position: 'relative',
        justifyContent: 'center'
      }}
    >
      <Icon name='comments outline' />
      {unreadCount > 0 && (
        <Label
          color='red'
          circular
          content={unreadCount > 99 ? '99+' : unreadCount}
          aria-label={t('UnreadMessages', {count: unreadCount})}
          style={{
            fontSize: 7,
            lineHeight: 1,
            minWidth: 15,
            minHeight: 15,
            padding: 4,
            position: 'absolute',
            right: 1,
            top: 11
          }}
        />
      )}
    </Menu.Item>
  );

  return <Popup content={label} position='bottom center' trigger={trigger} />;
};

const mapStateToProps = state => ({
  userProfile: state.user.userProfile,
  idToken: state.user.idToken,
  unreadCount: state.message.unreadCount || 0,
  latestIncomingMessage: state.message.latestIncomingMessage
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changePage: newPage => push(newPage),
      fetchConversations
    },
    dispatch
  );

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(ChatMenuItem))
);
